// src/components/common/HourSelector.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import './HourSelector.scss';

interface HourSelectorProps {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
  allowPrev?: boolean;
}

const HourSelector: React.FC<HourSelectorProps> = ({
  value,
  onChange,
  id,
  disabled = false,
  allowPrev = true
}) => {
  const [selectedHour, setSelectedHour] = useState<string>('');
  const [selectedMinute, setSelectedMinute] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('AM');
  
  // Flag to prevent circular updates when parsing external value changes
  const [isUpdatingFromExternal, setIsUpdatingFromExternal] = useState(false);
  
  // Dropdown states
  const [showHourDropdown, setShowHourDropdown] = useState(false);
  const [showMinuteDropdown, setShowMinuteDropdown] = useState(false);
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);
  const periodRef = useRef<HTMLDivElement>(null);

  // Función para convertir hora de 12h a 24h
  const convertTo24Hour = (hour12: number, period: string): number => {
    if (period === 'AM') {
      return hour12 === 12 ? 0 : hour12;
    } else {
      return hour12 === 12 ? 12 : hour12 + 12;
    }
  };

  // Función para convertir hora de 24h a 12h
  const convertTo12Hour = (hour24: number): { hour: number; period: string } => {
    if (hour24 === 0) {
      return { hour: 12, period: 'AM' };
    } else if (hour24 < 12) {
      return { hour: hour24, period: 'AM' };
    } else if (hour24 === 12) {
      return { hour: 12, period: 'PM' };
    } else {
      return { hour: hour24 - 12, period: 'PM' };
    }
  };

  // Obtener hora y minutos actuales
  const now = new Date();
  const currentHour24 = now.getHours();
  const currentMinutes = now.getMinutes();

  // Función para verificar si una combinación hora/periodo está disponible
  const isTimeAvailable = useCallback((hour12: number, period: string, minute?: number): boolean => {
    if (allowPrev) return true;

    const hour24 = convertTo24Hour(hour12, period);
    
    // Si la hora es futura, está disponible
    if (hour24 > currentHour24) return true;
    
    // Si es la hora actual, verificar minutos
    if (hour24 === currentHour24) {
      if (minute === undefined) return true; // Para validación de hora solamente
      return minute > currentMinutes;
    }
    
    // Si es hora pasada, no está disponible
    return false;
  }, [allowPrev, currentHour24, currentMinutes]);

  // Función para verificar si un período (AM/PM) tiene horas disponibles
  const isPeriodAvailable = useCallback((period: string): boolean => {
    if (allowPrev) return true;
    
    // Verificar si al menos una hora en este período está disponible
    for (let hour = 1; hour <= 12; hour++) {
      if (isTimeAvailable(hour, period)) {
        return true;
      }
    }
    return false;
  }, [allowPrev, isTimeAvailable]);

  // Generar períodos disponibles
  const getAvailablePeriods = useCallback((): string[] => {
    const periods = [];
    if (isPeriodAvailable('AM')) periods.push('AM');
    if (isPeriodAvailable('PM')) periods.push('PM');
    return periods;
  }, [isPeriodAvailable]);

  // Parsear el valor inicial
  useEffect(() => {
    if (value) {
      // Intentar parsear formato "HH:MM" (24h)
      const timeMatch = value.match(/^(\d{2}):(\d{2})$/);
      if (timeMatch) {
        const hour24 = parseInt(timeMatch[1]);
        const minute = timeMatch[2];
        const { hour, period } = convertTo12Hour(hour24);
        
        setIsUpdatingFromExternal(true);
        setSelectedHour(hour.toString());
        setSelectedMinute(minute);
        setSelectedPeriod(period);
        
        // Reset flag after state updates
        setTimeout(() => setIsUpdatingFromExternal(false), 0);
      }
    }
  }, [value]);

  // Actualizar el valor cuando cambian las selecciones (but not when updating from external)
  useEffect(() => {
    if (!isUpdatingFromExternal && selectedHour && selectedMinute && selectedPeriod) {
      const hour24 = convertTo24Hour(parseInt(selectedHour), selectedPeriod);
      const timeString = `${hour24.toString().padStart(2, '0')}:${selectedMinute}`;
      onChange(timeString);
    }
  }, [selectedHour, selectedMinute, selectedPeriod, onChange, isUpdatingFromExternal]);

  // Efecto para cambiar automáticamente el período si el actual no está disponible
  useEffect(() => {
    const availablePeriods = getAvailablePeriods();
    
    if (availablePeriods.length > 0 && !availablePeriods.includes(selectedPeriod)) {
      // Si el período actual no está disponible, cambiar al primero disponible
      setSelectedPeriod(availablePeriods[0]);
      // Resetear hora y minuto ya que el período cambió
      setSelectedHour('');
      setSelectedMinute('');
    }
  }, [selectedPeriod, allowPrev, currentHour24, currentMinutes, getAvailablePeriods]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (hourRef.current && !hourRef.current.contains(event.target as Node)) {
        setShowHourDropdown(false);
      }
      if (minuteRef.current && !minuteRef.current.contains(event.target as Node)) {
        setShowMinuteDropdown(false);
      }
      if (periodRef.current && !periodRef.current.contains(event.target as Node)) {
        setShowPeriodDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Helper function to check if dropdown would overflow and needs repositioning
  const getDropdownClasses = (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return '';
    
    const rect = ref.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const modalPadding = 40; // Account for modal padding
    
    // If this is in the rightmost third of the viewport or close to the right edge
    // Apply more aggressive left positioning
    if (rect.right > viewportWidth - modalPadding - 100) {
      return 'align-right';
    }
    
    return '';
  };

  // Generar opciones de horas (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  
  // Generar opciones de minutos (intervalos de 5)
  const minuteOptions = Array.from({ length: 12 }, (_, i) => i * 5);

  const availablePeriods = getAvailablePeriods();

  return (
    <div className="hour-selector" id={id}>
      <div className="time-inputs">
        {/* Selector de Hora */}
        <div className="dropdown-col" ref={hourRef}>
          <button
            type="button"
            className={`toggle-btn ${disabled ? 'disabled' : ''}`}
            onClick={() => !disabled && setShowHourDropdown(!showHourDropdown)}
            disabled={disabled}
          >
            <span>{selectedHour || 'HH'}</span>
            <FaChevronDown className={`chevron ${showHourDropdown ? 'open' : ''}`} />
          </button>
          {showHourDropdown && !disabled && (
            <div className={`dropdown-menu-col ${getDropdownClasses(hourRef)}`}>
              {hourOptions.map(hour => {
                const isAvailable = isTimeAvailable(hour, selectedPeriod);
                return (
                  <div
                    key={hour}
                    className={`dropdown-item ${selectedHour === hour.toString() ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                    onClick={() => {
                      if (isAvailable) {
                        setSelectedHour(hour.toString());
                        setShowHourDropdown(false);
                      }
                    }}
                  >
                    {hour}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <span className="time-separator">:</span>

        {/* Selector de Minutos */}
        <div className="dropdown-col" ref={minuteRef}>
          <button
            type="button"
            className={`toggle-btn ${disabled || !selectedHour ? 'disabled' : ''}`}
            onClick={() => !disabled && selectedHour && setShowMinuteDropdown(!showMinuteDropdown)}
            disabled={disabled || !selectedHour}
          >
            <span>{selectedMinute || 'MM'}</span>
            <FaChevronDown className={`chevron ${showMinuteDropdown ? 'open' : ''}`} />
          </button>
          {showMinuteDropdown && !disabled && selectedHour && (
            <div className={`dropdown-menu-col ${getDropdownClasses(minuteRef)}`}>
              {minuteOptions.map(minute => {
                const minuteStr = minute.toString().padStart(2, '0');
                const isAvailable = isTimeAvailable(parseInt(selectedHour), selectedPeriod, minute);
                return (
                  <div
                    key={minute}
                    className={`dropdown-item ${selectedMinute === minuteStr ? 'active' : ''} ${!isAvailable ? 'disabled' : ''}`}
                    onClick={() => {
                      if (isAvailable) {
                        setSelectedMinute(minuteStr);
                        setShowMinuteDropdown(false);
                      }
                    }}
                  >
                    {minuteStr}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Selector de AM/PM */}
        {availablePeriods.length > 1 ? (
          <div className="dropdown-col" ref={periodRef}>
            <button
              type="button"
              className={`toggle-btn ${disabled ? 'disabled' : ''}`}
              onClick={() => !disabled && setShowPeriodDropdown(!showPeriodDropdown)}
              disabled={disabled}
            >
              <span>{selectedPeriod}</span>
              <FaChevronDown className={`chevron ${showPeriodDropdown ? 'open' : ''}`} />
            </button>
            {showPeriodDropdown && !disabled && (
              <div className={`dropdown-menu-col ${getDropdownClasses(periodRef)}`}>
                {availablePeriods.map((period: string) => (
                  <div
                    key={period}
                    className={`dropdown-item ${selectedPeriod === period ? 'active' : ''}`}
                    onClick={() => {
                      const newPeriod = period;
                      setSelectedPeriod(newPeriod);
                      setShowPeriodDropdown(false);
                      
                      // Si la hora actual no está disponible con el nuevo período, resetearla
                      if (selectedHour && !isTimeAvailable(parseInt(selectedHour), newPeriod)) {
                        setSelectedHour('');
                        setSelectedMinute('');
                      }
                    }}
                  >
                    {period}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : availablePeriods.length === 1 ? (
          // Si solo hay un período disponible, mostrarlo como texto
          <div className="period-display">
            {availablePeriods[0]}
          </div>
        ) : (
          // Si no hay períodos disponibles, mostrar mensaje
          <div className="no-time-available">
            No disponible
          </div>
        )}
      </div>
    </div>
  );
};

export default HourSelector;
